import { LandrBrand } from 'constants/brands';
import React from 'react';
import { Box, Skeleton, useBreakpoint } from '@landr/maestro';
import { t } from '@lingui/macro';
import LinkWithArrow from 'components/LinkWithArrow/LinkWithArrow';
import { ChartEntry, ChartVariants } from '../Charts.types';
import { ChartLink, ChartWrapper, ChartHeading } from '../Charts.styled';
import { ChartItem } from './ChartItem';

type ChartInnerProps = {
    chart: ChartEntry[];
    heading?: React.ReactElement;
    linkText: string;
    linkPath: string;
    isLoading: boolean;
    isMobile: boolean;
    type: ChartVariants;
};

export const _Chart: React.FC<ChartInnerProps> = ({
    chart,
    heading = undefined,
    linkText,
    linkPath,
    isLoading,
    isMobile,
    type,
}) => {
    return (
        <ChartWrapper>
            {!isMobile && (
                <ChartHeading as="h3" size="xl">
                    {heading}
                </ChartHeading>
            )}
            <div>
                <Box alignSelf="start">
                    {!isLoading &&
                        chart?.map((chartEntry, index) => (
                            <ChartItem
                                key={chartEntry.guid}
                                index={index}
                                chartEntry={chartEntry}
                                isMobile={isMobile}
                                type={type}
                            />
                        ))}
                    {isLoading && !isMobile && (
                        <>
                            <Skeleton
                                height="54px"
                                width="100%"
                                mb="41px"
                                mt="20px"
                            />
                            <Skeleton height="54px" width="100%" mb="41px" />
                            <Skeleton height="54px" width="100%" mb="41px" />
                            <Skeleton height="54px" width="100%" mb="41px" />
                            <Skeleton height="54px" width="100%" mb="41px" />
                        </>
                    )}
                    {isLoading && isMobile && (
                        <>
                            <Skeleton
                                height="42px"
                                width="100%"
                                mb="41px"
                                mt="20px"
                            />
                            <Skeleton height="42px" width="100%" mb="41px" />
                            <Skeleton height="42px" width="100%" mb="41px" />
                            <Skeleton height="42px" width="100%" mb="41px" />
                            <Skeleton height="42px" width="100%" mb="33px" />
                        </>
                    )}
                </Box>
                <ChartLink>
                    <LinkWithArrow to={linkPath} ctaCopy={linkText} />
                </ChartLink>
            </div>
        </ChartWrapper>
    );
};

type ChartProps = {
    chart: ChartEntry[];
    heading?: React.ReactElement;
    linkText: string;
    linkPath: string;
    type: ChartVariants;
    isLoading: boolean;
};

export const Chart: React.FC<ChartProps> = (props) => {
    const isMobile = useBreakpoint('lg');

    return <_Chart {...props} isMobile={isMobile} />;
};

export const TopPacksChart = ({
    isLoading,
    chart,
    heading,
    linkPath,
}: {
    isLoading: boolean;
    chart: ChartEntry[];
    heading?: React.ReactElement;
    linkPath: string;
}) => (
    <Chart
        isLoading={isLoading}
        chart={chart}
        heading={heading}
        linkText={t`More packs`}
        linkPath={linkPath}
        type={ChartVariants.Packs}
    />
);

export const TopLabelsChart = ({
    isLoading,
    chart,
    heading,
    linkPath,
}: {
    isLoading: boolean;
    chart: ChartEntry[];
    heading?: React.ReactElement;
    linkPath: string;
}) => (
    <Chart
        isLoading={isLoading}
        chart={chart}
        heading={heading}
        linkText={t`More labels`}
        linkPath={linkPath}
        type={ChartVariants.Labels}
    />
);

export const LandrTopPacksChart = ({
    isLoading,
    chart,
    heading,
    linkPath,
}: {
    isLoading: boolean;
    chart: ChartEntry[];
    heading?: React.ReactElement;
    linkPath: string;
}) => (
    <Chart
        isLoading={isLoading}
        chart={chart}
        heading={heading}
        linkText={t`More ${LandrBrand} exclusives`}
        linkPath={linkPath}
        type={ChartVariants.LandrTopPacks}
    />
);
